import { useEffect, useMemo } from "react";
import moment from "moment";
import { useGetTopicClusteringTreeCounts } from "@/utils/react-query-hooks/aiAnnotation";
import { useAggrMetricsAcrossTimeAndDimension } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetLineChart/query/useAggrMetricsAcrossTimeAndDimension";
import { CONFIG } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/CONFIG";
import { scoreTree, periodGrain } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/scoreFindings";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";

/**
 * Priorities on server-side aggregates, as one hook: the requests and the
 * scoring. Every Priorities surface (the field, the table,
 * the category cards) calls this with the same scope and react-query
 * answers each request once.
 *
 * Requests, all over the scope WITHOUT the period (momentum reads the whole
 * series and slices it itself around the selected period, as in V1):
 *   1. the counts endpoint with `withPriorityStats` — per node: count,
 *      majority signal, datasets, distinct documents, customer voice;
 *   2. the sub-topic history series — per sub-topic (and the root Unplaced
 *      row) a count per period at the period's grain, back over
 *      HISTORY_MAX_PERIODS, via the annotation time-series ES branch;
 *   3. (`withSignalSeries`) the same series per signal — the category
 *      cards' share-of-all and its change.
 *
 * The scoring is scoreFindings, fed its `stats` shape directly. Those five
 * numbers and a per-period series are exactly what its maths reduces an
 * observation array to, and exactly what the two requests above already
 * return — so nothing is unrolled: the tree handed to the scorer is ~1.4k
 * objects rather than the ~70k pseudo observations this hook used to
 * fabricate on every scope change.
 *
 * The one thing the aggregates cannot give is a per-dataset series, so
 * `countsByDataset` is null and the dataset-level readings (rising-in, and
 * the import-dated momentum suppression) stay off rather than being faked
 * from a made-up attribution. Both would need per-dataset counts per node
 * from the counts endpoint.
 *
 * Returns { counts, historyTree, scored: { priorities, wins, meta } | null,
 *           seriesByNode: { [subtopicId | __unplaced:<topicId> | __unplaced]: { [periodKey]: n } },
 *           signalSeries: { [signalId]: { [periodKey]: n } },
 *           periodKeys: every period in the history window in order (zero
 *           months included — the series only hold non-zero counts),
 *           grain, loading, signalsLoading }
 *
 * `loading` covers request 1 + 2 and the scoring, i.e. everything every
 * caller needs. The signal series (request 3) is reported separately as
 * `signalsLoading` because only some surfaces ask for it: a caller that
 * folded it into one flag would hold its whole section behind a request
 * most of its widgets don't read. Requests 1 + 2 are shared with the
 * Priorities page and come from cache, so the section paints at once and
 * the signal widgets fill in when their series lands.
 */

// The counts endpoint's aggregates ARE the reductions the scorer would
// otherwise compute by walking an array: `annotationCount` is its count,
// `docs` its distinct documents, `voice` its two voice tallies, `datasets`
// its dataset set, and the time-series row its counts per period. So hand
// the scorer those directly — nothing is unrolled here any more.
//
// `countsByDataset` stays null: the payload carries a node's dataset LIST
// but no per-dataset series. That turns the dataset-level readings off
// (rising-in, and the import-dated momentum suppression) rather than
// faking them from a made-up attribution.
const statsFromNode = (node, series) => ({
  n: node?.annotationCount || 0,
  docs: node?.docs || 0,
  classified: (node?.voice?.customer || 0) + (node?.voice?.nonCustomer || 0),
  customer: node?.voice?.customer || 0,
  datasets: node?.datasets || [],
  countsByKey: series || {},
  countsByDataset: null,
  fallbackDatedN: 0,
});

// Counts per period add, so a topic's series is its rows' plus its parked row's.
const addCounts = (into, counts) => {
  for (const [k, n] of Object.entries(counts || {})) into[k] = (into[k] || 0) + n;
  return into;
};

// rows [{ timeInterval, dimensions: [{ value, count }] }] → value → { timeInterval: count }
const seriesByValue = (rows) => {
  const out = {};
  for (const row of rows || []) {
    for (const d of row.dimensions || []) {
      if (!d.count) continue;
      (out[String(d.value)] ||= {})[row.timeInterval] = d.count;
    }
  }
  return out;
};

// Content types whose "date that matters" is the document's publish date —
// import time, not customer time (the BE's fallback when the template has
// no date field: resolveDateFieldMap ANNOTATION_DATE_FALLBACK). A finding
// mostly made of those has no real timeline, so the scorer suppresses its
// momentum (reason "import-dated") rather than reading the upload schedule
// as a trend. V1 derives the same set in TopicClusteringTreeView/index.js.
const fallbackDatedDatasetsOf = (dateFieldMap) =>
  new Set(
    Object.entries(dateFieldMap || {})
      .filter(([, entry]) => entry?.valuePath === "kp_date_published")
      .map(([contentType]) => contentType)
  );

// The scorer found no earlier period with observations: one month of data
// and nothing before it. Nothing time-wise can be read then — no trend, no
// momentum, so no zones — and every surface that says so (the blurred
// widgets, the fires stat) reads this one flag.
export const isSinglePeriod = (meta) => meta?.historyPeriods === 0;

export const usePrioritiesScoring = ({
  topicClusteringLens,
  historyFilters,
  period,
  dateFieldMap,
  signalMetaById,
  scoreGrain = "findings",
  trendWindow = 2,
  // The comparison period by key (scoreTree's `compareKey`); undefined
  // leaves the choice to `trendWindow`.
  compareKey,
  withSignalSeries = false,
  onCountsMeta,
}) => {
  const categoryTheme = useSignalCategories();
  const grain = periodGrain(period) || "month";
  const fallbackDatedDatasets = useMemo(() => fallbackDatedDatasetsOf(dateFieldMap), [dateFieldMap]);

  const { data: counts, isLoading: countsLoading } = useGetTopicClusteringTreeCounts({
    topicClusteringLens,
    activeFilters: historyFilters,
    withPriorityStats: true,
  });
  useEffect(() => {
    if (counts) onCountsMeta?.(counts);
  }, [counts, onCountsMeta]);

  // The history window: HISTORY_MAX_PERIODS grains before the period, to its end.
  const timelineRange = useMemo(() => {
    if (!period?.from || !period?.to) return undefined;
    return { from: moment(period.from).subtract(CONFIG.HISTORY_MAX_PERIODS, grain === "week" ? "weeks" : grain === "year" ? "years" : "months").toISOString(), to: period.to };
  }, [period, grain]);
  const seriesCommon = {
    contentType: "annotations",
    dateValuePathByContentType: dateFieldMap,
    timelineRange,
    interval: grain,
    activeFilters: historyFilters,
    includeNullDimensions: false,
  };
  const { data: seriesRows, isLoading: seriesLoading } = useAggrMetricsAcrossTimeAndDimension({
    ...seriesCommon,
    vizCardId: "topicsV2:priorities:subtopics",
    dimensionValuePath: { dimensionType: "topicClusteringNode", level: "subtopic" },
    queryOptions: { enabled: !!timelineRange && !!dateFieldMap },
  });
  const { data: signalRows, isLoading: signalLoading } = useAggrMetricsAcrossTimeAndDimension({
    ...seriesCommon,
    vizCardId: "topicsV2:priorities:signals",
    dimensionValuePath: "annotations.tags.tags",
    queryOptions: { enabled: withSignalSeries && !!timelineRange && !!dateFieldMap },
  });

  // series: nodeId → { timeKey: count } (sub-topics, parked pseudo nodes and
  // the root Unplaced row alike).
  const seriesByNode = useMemo(() => seriesByValue(seriesRows), [seriesRows]);
  const signalSeries = useMemo(() => seriesByValue(signalRows), [signalRows]);
  // The ES branch emits every period in the range (min_doc_count 0), so the
  // rows carry the zero months the folded series drop.
  const periodKeys = useMemo(() => (signalRows || []).map((row) => row.timeInterval), [signalRows]);

  const historyTree = useMemo(() => {
    if (!counts) return null;
    const tree = (counts.tree || []).map((topic) => {
      const children = (topic.children || [])
        .filter((sub) => sub.annotationCount > 0)
        .map((sub) => ({
          nodeId: sub.nodeId,
          label: sub.label,
          description: sub.description,
          signalId: sub.signalId,
          // Carried for the side panel a row click opens, which reads the
          // node itself (its observation count and datasets), not the stats.
          annotationCount: sub.annotationCount,
          datasets: sub.datasets || [],
          stats: statsFromNode(sub, seriesByNode[String(sub.nodeId)]),
        }));
      const parkedStats = statsFromNode(
        { ...topic.parked, datasets: topic.datasets },
        seriesByNode[`__unplaced:${topic.nodeId}`]
      );
      // The topic scored as a node (the Topics grain): its OWN aggregates —
      // `docs` is distinct across the whole topic, which is not the sum of
      // its rows' — over the series its rows and its parked row add up to.
      const topicCounts = {};
      for (const child of children) addCounts(topicCounts, child.stats.countsByKey);
      addCounts(topicCounts, parkedStats.countsByKey);
      return {
        nodeId: topic.nodeId,
        label: topic.label,
        description: topic.description,
        annotationCount: topic.annotationCount,
        datasets: topic.datasets || [],
        children,
        parkedStats,
        bucketStats: statsFromNode(topic, topicCounts),
      };
    });
    return {
      tree,
      unplacedStats: statsFromNode({ annotationCount: counts.unplacedCount || 0 }, seriesByNode.__unplaced),
    };
  }, [counts, seriesByNode]);

  const scored = useMemo(
    () =>
      historyTree
        ? scoreTree({ historyTree, period, signalMetaById, signalCategories: categoryTheme.list, fallbackDatedDatasets, grain: scoreGrain, window: trendWindow, compareKey })
        : null,
    [historyTree, period, signalMetaById, categoryTheme, fallbackDatedDatasets, scoreGrain, trendWindow, compareKey]
  );

  return {
    counts,
    historyTree,
    scored,
    seriesByNode,
    signalSeries,
    periodKeys,
    grain,
    loading: countsLoading || seriesLoading || !scored,
    signalsLoading: withSignalSeries && signalLoading,
  };
};
